<script setup lang="ts">
import type { PathwayEvent, Step } from '@/types/shared/pathway-event'
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import StarsBalancePill from '@/components/StarsBalancePill.vue'
import { computed, ref } from 'vue'
import { SPECIAL_ECOMMERCE_ITEM_ID } from '@/types/shared/player-config'
import { useConfig } from '@/composables/game/config'
import BaseButton from '@/components/base/BaseButton.vue'
import PaymentButtons from '@/components/PaymentButtons.vue'
import { useEcommercePrice } from '@/composables/game/ecommerce'
import { useClientDeals } from '@/composables/client-deals'
import { usePlayer } from '@/composables/game/player'
import { useConfetti } from '@/composables/game/confetti'

const props = defineProps<{
	show: boolean
	step: Step & { index: number }
	clientDeal: PathwayEvent
}>()
const emit = defineEmits<{
	(e: 'hide'): void
	(e: 'purchaseDone'): void
}>()

const { config } = useConfig()
const { getStarPrice } = useEcommercePrice()
const { goldenCoinValue } = usePlayer()
const { rewardImageMap, getCurrentStepConfig, getVisibleRewards, getRewardAmount } = useClientDeals()
const { showConfetti } = useConfetti()

const currentStepConfig = computed(() => getCurrentStepConfig(props.step.index))

const visibleRewards = computed(() => getVisibleRewards(props.step))

const maybeEcommerceItem = computed(() => config.value.game.ecommerce.items.find(item => item.id === `${SPECIAL_ECOMMERCE_ITEM_ID.CLIENT_DEAL_STEP}.${props.clientDeal.id}`))
const ecommerceItem = computed(() => {
  if (!maybeEcommerceItem.value) throw new Error('Ecommerce item not found with the id: ' + `${SPECIAL_ECOMMERCE_ITEM_ID.CLIENT_DEAL_STEP}.${props.clientDeal.id}`)
  return maybeEcommerceItem.value
})

const starPrice = computed(() => getStarPrice(ecommerceItem.value))
const purchased = ref(false)

function handlePurchase(result: { paid: boolean, nonce?: string }) {
  if (result.paid) {
    purchased.value = true
    showConfetti()
  }
}

function onClose() {
  if (purchased.value) {
    emit('purchaseDone')
  }
  emit('hide')
}
</script>

<template>
  <TheBottomSheet
    :show="show"
    bg-fade-class="z-[802]"
    wrapper-class="z-[802]"
    content-class="pt-6"
    @hide="onClose"
  >
    <div
      class="w-full relative"
    >
      <StarsBalancePill
        class="absolute left-0 top-0"
      />
      <img
        class="mx-auto size-[144px] object-cover"
        :src="currentStepConfig.image"
      >
      <Transition
        name="fade"
        mode="out-in"
      >
        <div
          v-if="purchased"
          class="mt-3 w-full flex flex-col items-center"
        >
          <div class="font-pilat text-center text-light-white text-[24px] leading-8 font-bold">
            Purchase successful!
          </div>
          <div class="mt-3 flex items-center justify-between">
            <div
              v-for="(rewardType, _index) in visibleRewards"
              :key="_index"
              class="flex items-center gap-x-1"
            >
              <img
                class="h-auto object-cover w-5"
                :src="rewardImageMap[rewardType as keyof typeof rewardImageMap]"
              >
              <span
                class="text-white font-bold text-sm"
              >{{ getRewardAmount(rewardType, step, goldenCoinValue) }}</span>
            </div>
          </div>
          <BaseButton
            class="mt-6 w-full"
            @click="onClose"
          >
            Close
          </BaseButton>
        </div>
        <div
          v-else
          class="mt-3 w-full flex flex-col items-center"
        >
          <div class="font-pilat text-center text-light-white text-[24px] leading-8 font-bold">
            {{ currentStepConfig.title }} = ${{ starPrice?.usdAmount.toFixed(2) }}
          </div>
          <div class="text-center text-base font-semibold text-white/60">
            {{ currentStepConfig.subtitle }}
          </div>
          <div class="mt-3 flex items-center justify-between">
            <div
              v-for="(rewardType, _index) in visibleRewards"
              :key="_index"
              class="flex items-center gap-x-1"
            >
              <img
                class="h-auto object-cover w-5"
                :src="rewardImageMap[rewardType as keyof typeof rewardImageMap]"
              >
              <span
                class="text-white font-bold text-sm"
              >{{ getRewardAmount(rewardType, step, goldenCoinValue) }}</span>
            </div>
          </div>

          <div class="mt-6 w-full">
            <PaymentButtons
              :item="ecommerceItem"
              @purchase="handlePurchase"
            />
          </div>
        </div>
      </Transition>
    </div>
  </TheBottomSheet>
</template>

<style scoped>

</style>